import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Toast — a short confirmation bar at the bottom of the screen, ported from
// the ops console (apps/ops/src/ui/opsUi.tsx).
//
// For outcomes that need no action, such as "Question deleted". A failure the
// admin must ACT on stays where it happened (on the row, or inside the modal),
// because a message that disappears after a few seconds is the wrong home for
// it.
// ---------------------------------------------------------------------------

type ToastFn = (message: string) => void;

// The default is a no-op, so a component rendered without the provider (a
// unit test, say) still works; it just shows nothing.
const ToastContext = createContext<ToastFn>(() => {});

const TOAST_MS = 2600;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  // A counter, so the same text twice in a row still restarts the timer and
  // re-announces — two questions deleted back to back should say so twice.
  const [serial, setSerial] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const toast = useCallback<ToastFn>((next) => {
    setMessage(next);
    setSerial((n) => n + 1);
  }, []);

  useEffect(() => {
    if (message == null) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), TOAST_MS);
    return () => clearTimeout(timer.current);
  }, [message, serial]);

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* The live region is ALWAYS mounted, and only its text changes: a region
          inserted together with its content is not reliably announced. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-8 z-[70] flex justify-center px-4"
      >
        {message != null && (
          <div
            key={serial}
            className="flex max-w-full items-center gap-2.5 rounded-control bg-ink px-[18px] py-3 text-sm text-white shadow-[0_12px_32px_-12px_rgba(28,36,32,0.4)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="shrink-0 text-[#8FD3A8]"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span className="min-w-0 break-words">{message}</span>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastFn {
  return useContext(ToastContext);
}
